import type { TFunction } from 'i18next';
import type { ApprovalAttributes } from '@/types/approval';

/** 인플루언서 PPL 정산(135) — 레거시 appr_edit_forms_influencer_ppl. 인플루언서 시스템 `Ep` 진입 전용 */
export const INFLUENCER_PPL_TYPE = 135;

export const isInfluencerPplType = (apvType: number): boolean =>
  apvType === INFLUENCER_PPL_TYPE;

/** 인플루언서 시스템이 레거시 작성 화면에 문서 일련번호를 `Ep` 로 넘김 */
export const INFLUENCER_PPL_ENTRY_QUERY_KEY = 'Ep';

export const INFLUENCER_PPL_LOAD_FAILED_MESSAGE =
  '인플루언서 월마감 보고서 로딩에 실패하였습니다.';
export const INFLUENCER_PPL_SEQUENCE_MISSING_MESSAGE =
  '인플루언서 월마감 리포트 로드에 실패하였습니다.';
export const INFLUENCER_PPL_PURPOSE_REQUIRED_MESSAGE =
  '진행 목적을 입력해주세요.';
export const INFLUENCER_PPL_DEFAULT_PURPOSE_KEY =
  '{{brand}} 아이템별 커뮤니케이션 지수 상승 및 브랜드 인지도 & 선호도 제고를 위해 시즌 주력 아이템-라이프스타일 연결하여  셀럽들에게 무가로 시딩/협찬 진행 SS 시즌 시작 전에 선전달하여 최대한 셀럽이 판매 매기에 맞게 라이프스타일에 맞는 컨텐츠로 노출되도록 유도. 컴백 등의 이슈 있는 셀럽(가수, 배우, 개그맨, 예능인 등)에게 무가로 시딩하여 다양한 채널(방송, 유튜브, SNS 등)에 노출함으로써 자발적인 확산 바이럴을 이끌어내고자 함';
export const INFLUENCER_PPL_LOADING_MESSAGE =
  '데이터를 불러오는 중입니다. 잠시 후 다시 시도해주세요.';

export type InfluencerPplDetailItem = {
  category?: string;
  price?: number | string;
  qty?: number | string;
  pricePortion?: number | string;
};

/** 「상세보기」 견적 상세내역 데이터 */
export type InfluencerPplDetailInfo = {
  details?: InfluencerPplDetailItem[];
  totalPrice?: number | string;
  totalQty?: number | string;
  totalPricePortion?: number | string;
};

export type InfluencerPplCategoryChart = {
  category?: string;
  pcs?: number | string;
  qty?: number | string;
  totalPrice?: number | string;
};

export type InfluencerPplCategoryGraph = {
  category?: string;
  date?: string[];
  /** 검색량(%) — 선 */
  search?: number[];
  /** 노출건수 — 막대 */
  graph?: number[];
};

export type InfluencerPplCategoryImages = {
  category?: string;
  images?: string[];
};

/** draft-source display.source — PPL 정산 리포트 원문 */
export type InfluencerPplReport = {
  brdCd?: string;
  brdNm?: string;
  season?: string;
  startDt?: string;
  endDt?: string;
  totalCnt?: number | string;
  tagPriceSum?: number | string;
  tagPricePortion?: number | string;
  catNms?: string[];
  domainNms?: string[];
  detailInfo?: InfluencerPplDetailInfo | null;
  categoryChart?: InfluencerPplCategoryChart[];
  categoryGraph?: InfluencerPplCategoryGraph[];
  categoryImages?: InfluencerPplCategoryImages[];
};

/**
 * attributes.detailInfo — 레거시 window.influencer 와 같은 모양.
 * 레거시 조회 화면이 이 값으로 그래프·「상세보기」를 그렸고, V2 조회도 레거시 문서와 같은 방식으로 읽음
 */
export type InfluencerPplSavedInfo = {
  brdCd?: string;
  brdNm?: string;
  startDt?: string;
  endDt?: string;
  season?: string;
  categories?: string[];
  totalCnt?: number | string;
  tagPriceSum?: number | string;
  tagPricePortion?: number | string;
  domains?: string[];
  categoryChart?: InfluencerPplCategoryChart[];
  detailInfo?: InfluencerPplDetailInfo | null;
  categoryImages?: InfluencerPplCategoryImages[];
  categoryGraph?: InfluencerPplCategoryGraph[];
};

export type InfluencerPplDraftSource = {
  code: number;
  attributes?: { documentSeq?: string };
  display?: { source?: InfluencerPplReport | null };
};

export type InfluencerPplState = {
  documentSeq: string | null;
  report: InfluencerPplReport | null;
  loading: boolean;
  /** 조회를 마쳤는데 리포트를 못 받음 — 상신 막음 */
  failed: boolean;
  purpose: string;
  /** 편집 재진입 시 저장 본문의 목적 문구 — 조회가 끝나면 기본 문구 대신 채움 */
  savedPurpose?: string;
  /** 조회 때 채운 기본 문구 — 이탈 경고 판정에서 사용자 입력으로 보지 않음 */
  defaultPurpose?: string;
  /** 조회로 채운 제목 — 이탈 경고 판정에서 사용자 입력으로 보지 않음 */
  autoSubject?: string;
};

export const createInfluencerPplState = (
  documentSeq: string | null = null,
  savedPurpose?: string,
): InfluencerPplState => ({
  documentSeq,
  report: null,
  loading: false,
  failed: false,
  purpose: '',
  savedPurpose,
});

export const getInfluencerPplEntrySequence = (
  query: Record<string, string | string[] | undefined>,
): string | undefined => {
  const raw = query[INFLUENCER_PPL_ENTRY_QUERY_KEY];
  const value = (Array.isArray(raw) ? raw[0] : raw)?.trim();
  return value || undefined;
};

export const parseInfluencerPplSequence = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => {
  const value = attributes?.documentSeq;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

export const parseInfluencerPplContent = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => {
  const content = attributes?.content;
  return typeof content === 'string' && content.trim() ? content : null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

export const parseInfluencerPplSavedInfo = (
  attributes: Record<string, unknown> | null | undefined,
): InfluencerPplSavedInfo | null => {
  const detailInfo = attributes?.detailInfo;
  return isRecord(detailInfo) ? (detailInfo as InfluencerPplSavedInfo) : null;
};

/** 2023년 초 레거시 문서는 detailInfo 에 견적 상세내역만 있음 */
export const pickInfluencerPplDetailInfo = (
  saved: InfluencerPplSavedInfo | null,
): InfluencerPplDetailInfo | null => {
  if (!saved) return null;
  if (isRecord(saved.detailInfo)) return saved.detailInfo;
  return Array.isArray((saved as InfluencerPplDetailInfo).details)
    ? (saved as InfluencerPplDetailInfo)
    : null;
};

/** 목적 칸 — 레거시 저장 본문은 class 없이 「목적」 머리글 옆 칸 */
export const INFLUENCER_PPL_PURPOSE_CLASS = 'ppl-purpose';

export const parseInfluencerPplPurpose = (
  content: string | null | undefined,
): string | undefined => {
  if (!content || typeof DOMParser === 'undefined') return undefined;
  const doc = new DOMParser().parseFromString(content, 'text/html');
  const cell =
    doc.querySelector(`td.${INFLUENCER_PPL_PURPOSE_CLASS}`) ??
    [...doc.querySelectorAll('#influencer_report_tbl th')].find(
      (th) => th.textContent?.trim() === '목적',
    )?.nextElementSibling;
  if (!cell) return undefined;
  // 2023년 초 레거시 문서는 입력칸을 글자로 바꾸지 않고 저장함
  const textarea = cell.querySelector('textarea');
  return (textarea ?? cell).textContent ?? undefined;
};

export const toInfluencerPplNumber = (
  value: number | string | null | undefined,
): number => {
  const n = parseInt(String(value ?? ''), 10);
  return Number.isNaN(n) ? 0 : n;
};

export const formatInfluencerPplAmount = (
  value: number | string | null | undefined,
): string => toInfluencerPplNumber(value).toLocaleString('en-US');

/** 비고의 마케팅 재고 매장 — 레거시 getRemark */
export const getInfluencerPplMarketingShop = (brdCd?: string): string => {
  if (brdCd === 'M') return 'M35';
  if (brdCd === 'I') return 'I0030';
  return '';
};

/** 레거시 getReportPurpose — 띄어쓰기 누락('유도컴백')만 고침. 작성자 언어로 채움 */
export const buildInfluencerPplDefaultPurpose = (
  brand: string,
  t: TFunction,
): string =>
  t(INFLUENCER_PPL_DEFAULT_PURPOSE_KEY, {
    brand,
    interpolation: { escapeValue: false },
  });

export const toInfluencerPplSavedInfo = (
  report: InfluencerPplReport,
): InfluencerPplSavedInfo => ({
  brdCd: report.brdCd,
  brdNm: report.brdNm,
  startDt: report.startDt,
  endDt: report.endDt,
  season: report.season,
  categories: report.catNms,
  totalCnt: report.totalCnt,
  tagPriceSum: report.tagPriceSum,
  tagPricePortion: report.tagPricePortion,
  domains: report.domainNms,
  categoryChart: report.categoryChart,
  detailInfo: report.detailInfo,
  categoryImages: report.categoryImages,
  categoryGraph: report.categoryGraph,
});

/** 이탈 경고 — 조회로 채운 목적(기본 문구·저장 문구)에서 바뀐 경우만. 다 지운 목적도 변경으로 구분 */
export const influencerPplKey = (
  state: InfluencerPplState | null | undefined,
): string => {
  if (!state?.report) return '';
  const auto = state.savedPurpose ?? state.defaultPurpose ?? '';
  return state.purpose === auto ? '' : JSON.stringify([state.purpose]);
};

/** 임시저장 버튼 없음. 레거시는 리포트 조회 실패 시 저장 함수가 멈췄음 — V2 는 안내하고 막음 */
export const checkInfluencerPplBeforeSave = (
  state: InfluencerPplState | null | undefined,
): string | null => {
  if (!state?.documentSeq) return INFLUENCER_PPL_SEQUENCE_MISSING_MESSAGE;
  if (state.loading) return INFLUENCER_PPL_LOADING_MESSAGE;
  if (!state.report) return INFLUENCER_PPL_LOAD_FAILED_MESSAGE;
  if (!state.purpose.trim()) return INFLUENCER_PPL_PURPOSE_REQUIRED_MESSAGE;
  return null;
};

export const buildInfluencerPplAttributes = (
  state: InfluencerPplState | null | undefined,
  content: string,
): ApprovalAttributes | null => {
  if (!state?.documentSeq || !state.report) return null;
  return {
    documentSeq: state.documentSeq,
    brand: state.report.brdNm ?? null,
    season: state.report.season ?? null,
    content,
    detailInfo: toInfluencerPplSavedInfo(state.report),
  };
};
